import React, { useEffect, useState } from 'react';
import { connect, MapDispatchToProps, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useGoogleLogin } from '@react-oauth/google';
import { Formik, FormikProps } from 'formik';
import { toFormikValidate } from 'zod-formik-adapter';

import { useIsSignedIn, useSignIn, useOauthConnect } from '@front/auth/src/authentication-context';

import { FORGOTTEN_PASSWORD_URL_BY_COUNTRY } from '@front/common/src/constants/country';
import { getCurrentCountry } from '@front/common/src/utils/country';
import { hasProProfileChoice, getAllPrestationsInBasket } from '@front/common/src/ducks/customer/basket-selectors';
import { BasketState } from '@front/common/src/ducks/customer/types';
import { OAuthLoginData, PrestationReference, User, Pro } from '@front/common/src/types/commons';
import { isCleaningReference } from '@front/common/src/utils/universes';
import { SIZES as SIZES_TEXT_BUTTON } from '@front/common/src/constants/link';

import { useGetPreviousPros } from '@front/customer/src/queries/pro-accounts-queries';
import { FetchPreviousProsParams } from '@front/customer/src/types/screens';
import { getSignInFormSchema, SignInFormType } from '@front/customer/src/screens/sign-in/form';

import { translate } from '@front/i18n';

import Button from '@front/web-ui/src/components/button/button';
import SimpleLink from '@front/web-ui/src/components/link/simple-link';
import Alert from '@front/web-ui/src/components/alert/alert';
import TextButton from '@front/web-ui/src/components/button/text-button';
import Separator from '@front/web-ui/src/components/separator/separator';
import SocialButton from '@front/web-ui/src/components/button/social-button';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import Screen from '@front/web-ui/src/components/screen/screen';
import Input from '@front/web-ui/src/components/input/input';

import { signInFunnel, trackSignInEvent } from '../../utils/tracking';
import { PREVIOUS_PROS_PATH, route, SIGNUP_CHOICE_PATH, SIGNUP_PATH } from '../../steps/steps';
import { getNextPath, getPreviousPath } from '../../utils/navigation';
import useOAuth from '../../hooks/use-oauth';
import { RootState } from '../../store';
import ducks from '../../ducks';
import { isInWebView } from '../../utils/webview';

const t = translate('screens.sign_in');

const SignInContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ForgotPasswordLink = styled.div`
  margin-bottom: 10px;
  align-self: flex-end;
`;

const SignInButton = styled(Button)`
  margin-top: 10px;
`;

const NotSubscribedWrap = styled.div`
  text-align: center;
  margin: 20px 0 0;
`;

const StyledText = styled(Text.MD)`
  margin-top: 20px;
`;

const StyledSocialButton = styled(SocialButton)`
  margin-top: 20px;
`;

const ErrorContainer = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;

const StyledSeparator = styled(Separator)`
  margin: 20px 0;
`;

type DispatchProps = {
  fetchPreviousPros: (params: FetchPreviousProsParams) => void;
};

type StateProps = {
  address: BasketState['address'];
  universe: BasketState['universe'];
  referringProParams?: BasketState['referringProParams'];
  withProProfileChoice: boolean;
  prestations?: PrestationReference[];
  weeklyRecurrence?: BasketState['weekly_recurrence'];
  duration?: BasketState['duration'];
};

type SignInProps = StateProps & DispatchProps;

export const SignIn = ({
  universe,
  address,
  referringProParams,
  withProProfileChoice,
  duration,
  prestations,
  weeklyRecurrence,
  fetchPreviousPros,
}: SignInProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const to = getNextPath(location);
  const from = getPreviousPath(location);
  const isSignedIn = useIsSignedIn();
  const [shouldFetchPreviousPros, setShouldFetchPreviousPros] = useState(false);
  const zipCode = address?.zip_code;
  const hasPets = useSelector((state: RootState) => state.basket.hasPets);
  const [googleSignInErrors, setGoogleSignInErrors] = useState<string>();

  const isInsideWebView = isInWebView();
  // We have the same function in useSignupNavigation but we do not want to reuse this hook because we don't want
  // to reuse the useEffect, maybe move the useEffect away from the hook?
  const gotoSignup = (omniAuthData?: OAuthLoginData, partialUser?: Partial<User>) => {
    navigate(route(SIGNUP_PATH), { state: { from, to, omniAuthData, partialUser } });
  };

  const navigateToNext = (previousPros?: Pro[]) => {
    if (!previousPros || previousPros.length === 0) {
      navigate(to || from, { replace: true });
    } else if (isCleaningReference(universe) && hasPets === undefined) {
      navigate(to || from, { replace: true });
    } else {
      navigate(route(PREVIOUS_PROS_PATH), { state: { from, to } });
    }
  };

  const onSignInSuccess = () => {
    signInFunnel(universe, from);
    if (!withProProfileChoice && !!zipCode) {
      setShouldFetchPreviousPros(true);
      // In this screen with use react-query but in the rest of the app we use redux so here we trigger two queries
      // to the back, one in react-query and one in redux so in the other screens we have the info about the previous
      // pros. It is not ideal but it generates too much work in the authentication work to also replace redux with
      // react-query in location selection
      fetchPreviousPros({
        universe,
        zipCode,
        referringPro: referringProParams?.slug,
        prestations,
        weeklyRecurrence,
        duration,
      });
    } else {
      navigateToNext();
    }
  };

  const { isFormSignInLoading, formSignInError, formSignIn } = useSignIn({ onSuccess: onSignInSuccess });

  const { oauthConnectError, oauthConnect } = useOauthConnect({
    onSuccess: onSignInSuccess,
    onPartialConnect: gotoSignup,
  });

  const {
    facebookLogin,
    facebookInitialized,
    triggerGoogleSignInBackendProcess,
    error: thirdPartyError,
    thirdPartyCalled,
    submitted,
    setSubmitted,
  } = useOAuth(oauthConnect);

  const triggerGoogleSignInProcess = useGoogleLogin({
    scope: 'email profile',
    onSuccess: ({ access_token: accesToken }) => {
      triggerGoogleSignInBackendProcess(accesToken);
    },
    onError: (error) => {
      setGoogleSignInErrors(error.error_description);
    },
  });

  const { isInitialLoading: arePreviousProsLoading } = useGetPreviousPros(
    {
      universe,
      zipCode,
      referringPro: referringProParams?.slug,
      prestations,
      weeklyRecurrence,
      duration,
    },
    {
      enabled: shouldFetchPreviousPros,
      onSuccess: navigateToNext,
    },
  );

  const isLoading = arePreviousProsLoading || isFormSignInLoading;
  const hasErrors = !!(thirdPartyError || oauthConnectError || formSignInError);

  const initialValues = { email: '', password: '' };

  const signInFormSubmit = (values: SignInFormType) => {
    trackSignInEvent({
      eventType: 'LogInAccountButtonClick',
      path: location.pathname,
      universe,
    });
    formSignIn(values);
    setSubmitted('form');
  };

  const openSignupScreen = () => {
    trackSignInEvent({ eventType: 'CreateAccountButtonClick', path: location.pathname, universe });
    navigate(route(SIGNUP_CHOICE_PATH), { state: { from, to } });
  };

  const onFacebookButtonClick = () => {
    trackSignInEvent({
      eventType: 'LogInFacebookButtonClick',
      path: location.pathname,
      universe,
    });
    facebookLogin();
  };

  const onGoogleButtonClick = () => {
    trackSignInEvent({
      eventType: 'LogInGoogleButtonClick',
      path: location.pathname,
      universe,
    });
    triggerGoogleSignInProcess();
  };

  useEffect(() => {
    if (isSignedIn && !submitted) {
      navigate(-1);
    }
  }, [isSignedIn, navigate, submitted]);

  return (
    <Screen title={t('.titles.main')}>
      <SignInContainer>
        <ErrorContainer>
          {googleSignInErrors && <Alert type='error' content={googleSignInErrors} />}
          {thirdPartyError && <Alert type='error' content={thirdPartyError} />}
          {formSignInError && <Alert type='error' content={formSignInError} />}
          {oauthConnectError && <Alert type='error' content={oauthConnectError} />}
        </ErrorContainer>
        <Formik<SignInFormType>
          onSubmit={signInFormSubmit}
          initialValues={initialValues}
          validate={toFormikValidate(getSignInFormSchema())}
          validateOnChange={false}
        >
          {({
            errors: formErrors,
            handleBlur,
            handleChange,
            handleSubmit,
            isValid,
            touched,
            values,
          }: FormikProps<SignInFormType>) => (
            <StyledForm onSubmit={handleSubmit} id='loginForm'>
              <Input
                autoComplete='email'
                error={touched.email && formErrors.email ? formErrors.email : ''}
                id='formSignInEmail'
                label={t('inputs.email.placeholder')}
                name='email'
                onBlur={handleBlur('email')}
                onChange={handleChange('email')}
                value={values.email}
                type='email'
              />
              <Input
                type='password'
                autoComplete='password'
                error={touched.password && formErrors.password ? formErrors.password : ''}
                id='formSignInPassword'
                label={t('inputs.password.placeholder')}
                name='password'
                onBlur={handleBlur('password')}
                onChange={handleChange('password')}
                value={values.password}
              />
              <ForgotPasswordLink>
                <SimpleLink
                  size={SIZES_TEXT_BUTTON.SMALL}
                  title={t('.forgot_password')}
                  href={`${window.wecasa.siteUrl}${window.wecasa.locale}/${FORGOTTEN_PASSWORD_URL_BY_COUNTRY[getCurrentCountry()]}`}
                >
                  {t('.forgot_password')}
                </SimpleLink>
              </ForgotPasswordLink>
              <SignInButton
                isDisabled={!isValid}
                size='lg'
                type='submit'
                isLoading={isLoading && submitted === 'form'}
                hasFullWidth={true}
              >
                {t('.connect')}
              </SignInButton>
            </StyledForm>
          )}
        </Formik>
        <NotSubscribedWrap>
          <StyledText>{`${t('.no_account_question')}\u00a0`}</StyledText>
          <TextButton size='sm' label={t('.create_account_alt')} onClick={openSignupScreen}>
            {t('.create_account_alt')}
          </TextButton>
        </NotSubscribedWrap>
        <StyledSeparator text={t('base.or')} />
        <SocialButton
          provider='facebook'
          onPress={onFacebookButtonClick}
          isDisabled={!facebookInitialized}
          isLoading={thirdPartyCalled === 'facebook' && !hasErrors}
        />
        {!isInsideWebView && (
          <StyledSocialButton
            provider='google'
            onPress={onGoogleButtonClick}
            isLoading={thirdPartyCalled === 'google' && !hasErrors}
          />
        )}
      </SignInContainer>
    </Screen>
  );
};

export const mapStateToProps = (state: RootState) => ({
  address: state.basket.address,
  universe: state.basket.universe,
  referringProParams: state.basket.referringProParams,
  withProProfileChoice: hasProProfileChoice(state),
  prestations: getAllPrestationsInBasket(state),
  weeklyRecurrence: state.basket.weekly_recurrence,
  duration: state.basket.duration,
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, {}> = {
  fetchPreviousPros: ducks.proAccounts.fetchPreviousPros,
};

export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
